import {useDataQuery} from '@tnc/shared';
import clsx from 'clsx';
import {useState} from 'react';
import {useNavigate} from 'react-router';

import {createDataTableConfig} from '@/lib/helpers/config';

import {useAuth} from '@/providers/components/authProvider';

import {SearchAndSelectComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

type DistributorBasicT = {
  distributor_id: number;
  distributor_name: string;
};

type RetailerBasicT = {
  retailer_id: number;
  retailer_name: string;
};

function AdminInvoicesPage() {
  // Hooks
  const {isAdministrator} = useAuth();
  const navigate = useNavigate();

  // State
  const [promptNavModal, setPromptNavModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<'distributor' | 'retailer'>('distributor');
  const [promptSelectModal, setPromptSelectModal] = useState(false);

  const {data: distributors} = useDataQuery({
    table: 'Distributor',
    columns: ['distributor_id', 'distributor_name'],
    searchColumn: 'distributor_name',
    searchTerm,
    enabled: true,
  });

  const {data: retailers} = useDataQuery({
    table: 'Retailer',
    columns: ['retailer_id', 'retailer_name'],
    searchColumn: 'retailer_name',
    searchTerm,
    enabled: true,
  });

  // Data Table
  const dataTableConfig = createDataTableConfig({
    table: 'Invoices',
    title: 'Invoice Summary',
    columns: ['invoice_id', 'invoice_due_amount', 'invoice_status', 'invoice_due_date', 'invoice_date_issued'],
    columnsDisplay: [
      {
        header: 'Status',
        field: 'invoice_status',
        render: invoice => (
          <span
            className={`badge bg-${
              invoice.invoice_status === 'success'
                ? 'success'
                : invoice.invoice_status === 'pending'
                  ? 'warning'
                  : 'danger'
            } text-white`}
          >
            {invoice.invoice_status}
          </span>
        ),
      },
      {header: 'Issued Date', field: 'invoice_date_issued'},
      {header: 'Due Date', field: 'invoice_due_date'},
      {
        header: 'Total Amount',
        field: 'invoice_due_amount',
        render: invoice => `$${invoice.invoice_due_amount.toFixed(2)}`,
      },
    ],
    actionsDisplay: [
      {
        icon: 'ri-eye-line',
        color: 'info',
        handler: invoice => navigate(`${invoice.invoice_id}`),
        permission: () => isAdministrator,
      },
    ],
    onAdd: () => setPromptNavModal(true),
  });

  /**
   * Functions
   */
  const handleSelectType = (type: 'distributor' | 'retailer') => {
    setSelectedType(type);
    setPromptNavModal(false);
    setPromptSelectModal(true);
  };

  const handleSelectDistributor = (distributor: DistributorBasicT) => {
    setPromptSelectModal(false);
    navigate(`/admin/invoices/create/distributor/${distributor.distributor_id}`);
  };

  const handleSelectRetailer = (retailer: RetailerBasicT) => {
    setPromptSelectModal(false);
    navigate(`/admin/invoices/create/retailer/${retailer.retailer_id}`);
  };

  return (
    <>
      <Page currentPage="Invoices">
        <DataTable {...dataTableConfig} />
      </Page>

      <SearchAndSelectComponent<DistributorBasicT | RetailerBasicT>
        isVisible={promptSelectModal}
        onClose={() => setPromptSelectModal(false)}
        onSearch={setSearchTerm}
        results={selectedType === 'distributor' ? (distributors?.data ?? []) : (retailers?.data ?? [])}
        onSelect={row => {
          if (typeof row === 'object' && 'distributor_id' in row) {
            handleSelectDistributor(row);
          } else if (typeof row === 'object' && 'retailer_id' in row) {
            handleSelectRetailer(row);
          }
        }}
        title={`Select ${selectedType}`}
        mapStructure={{
          displayFields: [
            {title: 'Name', field: selectedType === 'distributor' ? 'distributor_name' : 'retailer_name'},
          ],
          valueField: selectedType === 'distributor' ? 'distributor_id' : 'retailer_id',
        }}
      />

      {/* Modal to select wholesale or market positions to navigate to */}
      <div
        className={clsx(
          'hs-overlay',
          promptNavModal ? 'flex' : 'hidden',
          'ti-modal fixed inset-0 items-center justify-center bg-black bg-opacity-30'
        )}
      >
        <div className="ti-modal-box modal-dialog-centered mt-0 max-h-[80vh] w-full overflow-hidden !opacity-100 ease-out hs-overlay-open:mt-7 sm:w-1/2 md:w-1/3 lg:w-1/4">
          <div className="ti-modal-content flex h-full flex-col rounded-lg bg-white shadow-lg">
            <div className="ti-modal-header flex-shrink-0 border-b p-4">
              <h6 className="modal-title !text-default text-[1rem] font-semibold dark:text-defaulttextcolor/70">
                Select Business Type
              </h6>
            </div>
            <div className="ti-modal-body px-6 py-4">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 xl:col-span-6">
                  <button
                    className="ti-btn w-full cursor-not-allowed bg-gray-300 opacity-50"
                    onClick={() => handleSelectType('distributor')}
                    disabled
                  >
                    Distributor
                  </button>
                </div>
                <div className="col-span-12 xl:col-span-6">
                  <button className="ti-btn w-full bg-primary" onClick={() => handleSelectType('retailer')}>
                    Retailer
                  </button>
                </div>
              </div>
            </div>
            <div className="ti-modal-footer flex-shrink-0 border-t p-4">
              <button className="ti-btn w-full bg-danger" onClick={() => setPromptNavModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminInvoicesPage;
